import maplibregl from 'maplibre-gl';
import { setMapInstance } from '../compat/LeafletShim.js';
import { w, mapStartView, displayLocale } from './market.js';
import * as units from './units.js';
import { isSegmentOnRoute } from './RoadQualityService.js';

/**
 * MapService — Phase 40 (MapLibre GL JS + AWS Location Service tiles)
 *
 * Public method signatures match the original Leaflet version so UIController
 * and other consumers work without modification.
 */
export class MapService {
    constructor(containerId) {
        this.containerId = containerId;
        this.map = null;
        this.markers = [];
        this.windMarkers = [];
        this.gasStationMarkers = [];
        this.routeSourceAdded = false;
        this.roadQualitySourceAdded = false;
        this._routeLayerGroup = null;

        // AWS Location Service map config — Maps v2 (GeoMaps) style descriptor.
        // ZephyrMapKeyV2 is scoped to geo-maps:* (resourceless, per-account),
        // unlike the old ZephyrMapKey which was tied to a single named "Maps"
        // resource (geo:GetMap* on map/ZephyrMap) and can't reach /v2/... at
        // all. Old key/URL pattern kept live and untouched for rollback --
        // see git history / VERSIONS.md if this needs reverting.
        this.mapStyle = 'Standard';
        this.region = 'us-east-1';
        this.apiKey = 'v1.public.eyJqdGkiOiIyYmEyMGI1OS02NzUzLTQ0MTktYjA3Mi1jNTZhZWRmZDY5MDMifaPRzlLe2wkWDp8rVyPPE_GS5b4DHbCgu0pP5vG7JonP3eSlUEdfI6cXwqS5VFHXqB1JfFUlxR-_UL9xEY1RbMD6PuswUWDTpyH1IS-YEjgVxZAKWqYoeBy4nYjYZFiTdHzXaQRhZONcwjQoYCKN5gKawN2H4p_LKBlmL2WdqNEP70Bx0cCtPPPXQCdJGC7grTf_6wXvi9S7RpZU8Gm2fz_b2IppHCm3v3QciNzb1x8HiNu5ZMX9ee8KaYBtwssk4MqZeUGtj9EdNtQ6HbvKG2ralnpDfhPfT9wGoKWhWNM-RTTdwCCR_mF2T--Z50a4dYySCxdksdt-qubL_woWWO4.ZWU0ZWIzMTktMWRhNi00Mzg0LTllMzYtNzlmMDU3MjRmYTkx';
    }

    init() {
        if (this.map) {
            console.warn('Map already initialized, skipping');
            return this.map;
        }

        // traffic=All: congestion coloring + construction zones + incidents.
        // Congestion-only is a one-word change here if All ends up too busy
        // alongside the route line/grade coloring/wind markers.
        const styleUrl = `https://maps.geo.${this.region}.amazonaws.com/v2/styles/${this.mapStyle}/descriptor?key=${this.apiKey}&terrain=Terrain3D&traffic=All`;

        const start = mapStartView();
        this.map = new maplibregl.Map({
            container: this.containerId,
            style: styleUrl,
            center: start.center,
            zoom: start.zoom,
            attributionControl: true
        });

        // The account's market can arrive after the map opens (first sign-in
        // on this device). Move to its country only if nothing has moved the
        // map since it opened: no user pan/zoom, route or location fix.
        let untouched = true;
        this.map.on('movestart', () => { if (this._movingToMarket) return; untouched = false; });
        window.addEventListener('zephyr:marketchange', () => {
            if (!untouched || !this.map) return;
            const view = mapStartView();
            this._movingToMarket = true;
            this.map.jumpTo({ center: view.center, zoom: view.zoom });
            this._movingToMarket = false;
        });

        this.map.addControl(new maplibregl.NavigationControl(), 'top-right');

        // Register with Leaflet shim so L.polyline/L.marker calls work
        setMapInstance(this.map);

        this.map.on('load', () => { this._addRouteSources(); });

        return this.map;
    }

    _addRouteSources() {
        this.map.addSource('route', {
            type: 'geojson',
            data: { type: 'Feature', geometry: { type: 'LineString', coordinates: [] } }
        });
        this.map.addLayer({
            id: 'route-line', type: 'line', source: 'route',
            paint: { 'line-color': '#667eea', 'line-width': 5, 'line-opacity': 0.7 }
        });
        this.routeSourceAdded = true;

        this.map.addSource('road-quality', {
            type: 'geojson',
            data: { type: 'FeatureCollection', features: [] }
        });
        this.map.addLayer({
            id: 'road-quality-line', type: 'line', source: 'road-quality',
            paint: {
                'line-color': ['match', ['get', 'color'], '#eab308', '#eab308', '#ef4444'],
                'line-width': ['match', ['get', 'color'], '#eab308', 10, 12],
                'line-opacity': 0.9
            },
            layout: { 'line-cap': 'round', 'line-join': 'round' }
        });
        this.roadQualitySourceAdded = true;
    }

    // ── Leaflet compatibility: routeLayer ────────────────────────────────

    get routeLayer() {
        if (!this._routeLayerGroup) {
            const self = this;
            this._routeLayerGroup = {
                _mlMap: self.map,
                _layers: [],
                clearLayers() {
                    this._layers.forEach(item => { if (item?.remove) item.remove(); });
                    this._layers.length = 0;
                    self.clearRoute();
                    self._clearAltRoutes();
                }
            };
        }
        this._routeLayerGroup._mlMap = this.map;
        return this._routeLayerGroup;
    }

    // ── Markers ──────────────────────────────────────────────────────────

    addMarker(lat, lng, label, color = 'blue') {
        const el = this._createMarkerEl(color);
        const marker = new maplibregl.Marker({ element: el }).setLngLat([lng, lat]).addTo(this.map);
        if (label) marker.setPopup(new maplibregl.Popup({ offset: 25 }).setHTML(label));
        this.markers.push(marker);
        return marker;
    }

    _createMarkerEl(color) {
        let icon = '📍', bgColor = color;
        if (color === 'green') { icon = '🏁'; bgColor = '#4CAF50'; }
        else if (color === 'red') { icon = '🏁'; bgColor = '#F44336'; }
        else if (color === 'orange') { icon = '⛽'; bgColor = '#FFC107'; }
        const el = document.createElement('div');
        el.innerHTML = `<div style="background:${bgColor};width:35px;height:35px;border-radius:50%;border:3px solid white;box-shadow:0 3px 8px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;font-size:18px;">${icon}</div>`;
        return el;
    }

    clearMarkers() { this.markers.forEach(m => m.remove()); this.markers = []; }

    // ── Route Drawing ────────────────────────────────────────────────────

    drawRoute(coordinates, options = {}) {
        this.clearRoute();
        const coords = coordinates.map(c => Array.isArray(c) ? [c[1], c[0]] : [c.lng, c.lat]);
        const geojson = { type: 'Feature', geometry: { type: 'LineString', coordinates: coords } };

        const apply = () => {
            this.map.getSource('route').setData(geojson);
            this.map.setPaintProperty('route-line', 'line-color', options.color || '#667eea');
            this.map.setPaintProperty('route-line', 'line-width', options.weight || 5);
        };

        if (this.routeSourceAdded) apply();
        else this.map.on('load', apply);

        this.fitRouteToView(coordinates);
        return geojson;
    }

    fitRouteToView(coordinates, options = {}) {
        if (!coordinates || coordinates.length === 0) return;
        const bounds = new maplibregl.LngLatBounds();
        coordinates.forEach(c => {
            if (Array.isArray(c)) bounds.extend([c[1], c[0]]);
            else bounds.extend([c.lng, c.lat]);
        });
        this.map.fitBounds(bounds, { padding: 50, ...options });
    }

    clearRoute() {
        if (this.routeSourceAdded && this.map?.getSource('route')) {
            this.map.getSource('route').setData({ type: 'Feature', geometry: { type: 'LineString', coordinates: [] } });
        }
    }

    // ── 3D Route Preview (Phase 45) ───────────────────────────────────────
    // User-controlled preview over the 3D terrain already enabled on this
    // map's style (terrain=Terrain3D, from the v2 GeoMaps migration): the
    // route loads fit to view with full free camera control (drag, zoom,
    // rotate, pitch never locked out), and Play advances the camera smoothly
    // along the actual route path -- every real coordinate, in order, not
    // jump-cutting to distant points -- so tiles load in small incremental
    // steps the whole way, the same way live turn-by-turn navigation already
    // moves the camera safely. This replaced an automated highlight-reel
    // design (jump-cutting to a handful of "interesting" stops chosen from
    // the route's grade data) that repeatedly crashed or rendered
    // incomplete/snapshot frames instead of an actual flythrough.

    /** Compass bearing (0-360) from one {lat,lng} point to the next. */
    getBearing(start, end) {
        const startLat = start.lat * Math.PI / 180;
        const startLng = start.lng * Math.PI / 180;
        const endLat = end.lat * Math.PI / 180;
        const endLng = end.lng * Math.PI / 180;

        const dLng = endLng - startLng;
        const x = Math.sin(dLng) * Math.cos(endLat);
        const y = Math.cos(startLat) * Math.sin(endLat) -
            Math.sin(startLat) * Math.cos(endLat) * Math.cos(dLng);

        return (Math.atan2(x, y) * 180 / Math.PI + 360) % 360;
    }

    /** Great-circle distance between two {lat,lng} points, in miles. */
    _haversineMiles(a, b) {
        const R = 3958.8;
        const lat1 = a.lat * Math.PI / 180, lat2 = b.lat * Math.PI / 180;
        const dLat = (b.lat - a.lat) * Math.PI / 180;
        const dLng = (b.lng - a.lng) * Math.PI / 180;
        const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
        return 2 * R * Math.asin(Math.sqrt(h));
    }

    /**
     * Load a route into the preview: fit the whole route in view, tilt into
     * a 3D angle, and set up the path data Play will animate along. Does
     * NOT start playing -- the user decides when. Returns false if the
     * route has no usable coordinates.
     */
    loadRoutePreview(route, { onProgress = () => {}, onPlayStateChange = () => {} } = {}) {
        const coordinates = (route.coordinates || []).map(c =>
            Array.isArray(c) ? { lat: c[0], lng: c[1] } : { lat: c.lat, lng: c.lng }
        );
        if (coordinates.length < 2) return false;

        // Cumulative distance (miles) at each coordinate, so a point at any
        // fraction of the trip can be found by interpolating between two
        // known coordinates instead of guessing at even index spacing.
        const cumMiles = [0];
        for (let i = 1; i < coordinates.length; i++) {
            cumMiles.push(cumMiles[i - 1] + this._haversineMiles(coordinates[i - 1], coordinates[i]));
        }

        this.closeRoutePreview();
        this._preview = {
            coordinates,
            cumMiles,
            totalMiles: cumMiles[cumMiles.length - 1],
            segIndex: 1,
            onProgress,
            onPlayStateChange,
            playing: false,
            hasStartedPlaying: false,
            speedMultiplier: 1,
            traveledMiles: 0,
            lastBearing: 0,
            rafId: null,
            lastFrameTime: null
        };

        this.fitRouteToView(coordinates);
        this.map.easeTo({ pitch: 45, duration: 800 });
        this._attachPreviewInteractionPause();
        this._attachContextLossSafety();

        // RV position marker: fixed orientation (no rotation) -- the asset
        // is a side-profile marketing graphic with the ZEPHYR wordmark on
        // it, so rotating it to face the route's actual direction would
        // look broken (sideways text, upside-down RV). It just moves.
        const el = document.createElement('img');
        el.src = '/assets/ZephyrRV.png';
        el.style.width = '56px';
        el.style.height = 'auto';
        el.style.filter = 'drop-shadow(0 2px 4px rgba(0,0,0,0.4))';
        this._preview.marker = new maplibregl.Marker({ element: el })
            .setLngLat([coordinates[0].lng, coordinates[0].lat])
            .addTo(this.map);

        return true;
    }

    /** Pause playback the moment the user actually touches the map themselves. */
    _attachPreviewInteractionPause() {
        const canvas = this.map.getCanvas();
        this._previewPauseHandler = () => this.pauseRoutePreview();
        ['mousedown', 'touchstart', 'wheel'].forEach(evt =>
            canvas.addEventListener(evt, this._previewPauseHandler, { passive: true })
        );
    }

    _detachPreviewInteractionPause() {
        if (!this._previewPauseHandler) return;
        const canvas = this.map.getCanvas();
        ['mousedown', 'touchstart', 'wheel'].forEach(evt =>
            canvas.removeEventListener(evt, this._previewPauseHandler)
        );
        this._previewPauseHandler = null;
    }

    /**
     * If the WebGL context dies mid-playback -- rugged terrain over a
     * mountainous route pulls in far heavier elevation tiles than flat
     * ground, and streaming those continuously during a multi-minute
     * playback session is real GPU pressure -- stop cleanly instead of
     * continuing to fire jumpTo() into a dead context.
     */
    _attachContextLossSafety() {
        const canvas = this.map.getCanvas();
        this._previewContextLostHandler = (e) => {
            e.preventDefault?.();
            this.pauseRoutePreview();
        };
        canvas.addEventListener('webglcontextlost', this._previewContextLostHandler);
    }

    _detachContextLossSafety() {
        if (!this._previewContextLostHandler) return;
        this.map.getCanvas().removeEventListener('webglcontextlost', this._previewContextLostHandler);
        this._previewContextLostHandler = null;
    }

    /**
     * The route coordinate at `miles` traveled, interpolated between the two
     * real points it falls between. `hintIndex` is a lower bound to resume
     * scanning from (progress only moves forward) -- callers that need a
     * position ahead of the "current" one (see the bearing lookahead in
     * playRoutePreview) pass a hint without disturbing the main cursor, so
     * looking ahead doesn't corrupt next frame's scan for the real position.
     */
    _coordAtDistance(miles, hintIndex) {
        const { coordinates, cumMiles, totalMiles } = this._preview;
        const target = Math.min(Math.max(miles, 0), totalMiles);
        let i = Math.max(hintIndex, 1);
        while (i < cumMiles.length - 1 && cumMiles[i] < target) i++;

        const segStart = cumMiles[i - 1], segEnd = cumMiles[i];
        const t = segEnd > segStart ? (target - segStart) / (segEnd - segStart) : 0;
        const a = coordinates[i - 1], b = coordinates[i];
        return {
            point: { lat: a.lat + (b.lat - a.lat) * t, lng: a.lng + (b.lng - a.lng) * t },
            index: i
        };
    }

    /**
     * Start (or resume) animating the camera along the route. Only pans the
     * camera position -- zoom and pitch are left alone after the first start
     * so the user's own framing persists across playback.
     *
     * Pacing is a fixed real-world speed (BASE_MILES_PER_SECOND), not a
     * fixed total duration -- a fixed *duration* meant ground-speed scaled
     * up with route length, so a long RV trip covered enormous real distance
     * every second at driving zoom and looked like warp speed no matter how
     * much the total was padded. A fixed speed keeps motion readable
     * regardless of trip length; MIN/MAX_DURATION_S bound it at the extremes
     * (a short route doesn't blink by, a very long one doesn't run forever).
     * The user's own speedMultiplier (setPreviewSpeed) scales on top of that.
     *
     * Camera updates are throttled to CAMERA_UPDATE_INTERVAL_MS via
     * setInterval rather than firing on every animation frame (~60/sec) --
     * a short session never ran long enough to notice, but the multi-minute
     * sessions slower pacing now produces were calling jumpTo() enough times
     * to accumulate real render/GPU strain and crash the browser. This cuts
     * update frequency roughly 9x while still reading as smooth motion.
     */
    playRoutePreview() {
        if (!this._preview || this._preview.playing) return;
        const p = this._preview;

        // loadRoutePreview() leaves the camera fit to the WHOLE route, which
        // for anything but a short trip is far too zoomed-out to read as
        // "driving along it" -- the path barely moves on screen and reads as
        // a stuck, jittery view. The first Play zooms in to a normal driving
        // altitude at the starting point; after that we never touch zoom
        // again, so any zoom the user dials in themselves sticks.
        if (!p.hasStartedPlaying) {
            p.hasStartedPlaying = true;
            const start = this._coordAtDistance(0, 1);
            p.segIndex = start.index;
            this.map.jumpTo({ center: [start.point.lng, start.point.lat], zoom: 13, pitch: 55 });
        }

        p.playing = true;
        p.lastFrameTime = null;
        p.onPlayStateChange(true);

        const BASE_MILES_PER_SECOND = 0.05;
        const MIN_DURATION_S = 45;
        const MAX_DURATION_S = 900;
        const CAMERA_UPDATE_INTERVAL_MS = 200;
        // Heading is computed toward a point this far ahead, not the very
        // next raw coordinate -- real route polylines have small lateral
        // wiggle point-to-point, and a bearing recomputed from a few feet of
        // travel each frame zig-zags visibly. Looking further ahead gives a
        // stable direction of travel instead of that jitter.
        const BEARING_LOOKAHEAD_MILES = 0.2;
        // For any route longer than ~45mi, MAX_DURATION_S was the constraint
        // actually in effect, not BASE_MILES_PER_SECOND -- so the last few
        // rounds of lowering the base speed did nothing for a real RV-length
        // route, and it stayed just as fast. This factor cuts the real,
        // final pace by 10x regardless of which bound above was governing,
        // which should also mean 10x less new terrain loaded per camera
        // update -- a likely contributor to the mountainous-route crash.
        const SLOWDOWN_FACTOR = 5;

        const baseDurationS = Math.min(
            Math.max(p.totalMiles / BASE_MILES_PER_SECOND, MIN_DURATION_S),
            MAX_DURATION_S
        ) * SLOWDOWN_FACTOR;

        const tick = () => {
            const p = this._preview;
            if (!p || !p.playing) return;
            const now = performance.now();
            if (p.lastFrameTime == null) p.lastFrameTime = now;
            const dt = now - p.lastFrameTime;
            p.lastFrameTime = now;

            const effectiveDurationS = baseDurationS / (p.speedMultiplier || 1);
            const milesPerMs = p.totalMiles / (effectiveDurationS * 1000);
            p.traveledMiles = Math.min(p.traveledMiles + dt * milesPerMs, p.totalMiles);

            const here = this._coordAtDistance(p.traveledMiles, p.segIndex);
            p.segIndex = here.index;
            const ahead = this._coordAtDistance(
                Math.min(p.traveledMiles + BEARING_LOOKAHEAD_MILES, p.totalMiles),
                here.index
            );
            if (ahead.point.lat !== here.point.lat || ahead.point.lng !== here.point.lng) {
                p.lastBearing = this.getBearing(here.point, ahead.point);
            }

            this.map.jumpTo({ center: [here.point.lng, here.point.lat], bearing: p.lastBearing });
            p.marker?.setLngLat([here.point.lng, here.point.lat]);
            p.onProgress(p.totalMiles > 0 ? p.traveledMiles / p.totalMiles : 1);

            if (p.traveledMiles >= p.totalMiles) {
                p.playing = false;
                p.onPlayStateChange(false);
                clearInterval(p.intervalId);
                p.intervalId = null;
            }
        };

        this._preview.intervalId = setInterval(tick, CAMERA_UPDATE_INTERVAL_MS);
    }

    /**
     * Set the playback speed multiplier (e.g. 1.5 or 2 for a faster preview,
     * 0.5 for slower). Takes effect immediately, even mid-playback.
     */
    setPreviewSpeed(multiplier) {
        if (this._preview) this._preview.speedMultiplier = multiplier;
    }

    getPreviewSpeed() {
        return this._preview?.speedMultiplier || 1;
    }

    /** Pause exactly where the camera currently sits; Play resumes from here. */
    pauseRoutePreview() {
        if (!this._preview || !this._preview.playing) return;
        this._preview.playing = false;
        if (this._preview.intervalId) clearInterval(this._preview.intervalId);
        this._preview.intervalId = null;
        this._preview.onPlayStateChange(false);
    }

    isRoutePreviewPlaying() {
        return !!this._preview?.playing;
    }

    /** Tear down the preview entirely (closing the overlay). */
    closeRoutePreview() {
        if (this._preview) {
            this.pauseRoutePreview();
            this._detachPreviewInteractionPause();
            this._detachContextLossSafety();
            this._preview.marker?.remove();
        }
        this._preview = null;
    }

    // ── Multi-Route Display ──────────────────────────────────────────────

    drawMultipleRoutes(routes, selectedIndex, onRouteClick, fitOptions = {}) {
        const colors = ['#1e88e5', '#48bb78', '#ed8936', '#e53e3e'];
        this._clearAltRoutes();

        routes.forEach((route, index) => {
            if (!route.coordinates || route.coordinates.length < 2) return;
            const coords = route.coordinates.map(c => Array.isArray(c) ? [c[1], c[0]] : [c.lng, c.lat]);
            const sourceId = `alt-route-${index}`;
            const layerId = `alt-route-line-${index}`;
            const isSelected = index === selectedIndex;

            if (this.map.getLayer(layerId)) this.map.removeLayer(layerId);
            if (this.map.getSource(sourceId)) this.map.removeSource(sourceId);

            this.map.addSource(sourceId, { type: 'geojson', data: { type: 'Feature', geometry: { type: 'LineString', coordinates: coords } } });
            this.map.addLayer({ id: layerId, type: 'line', source: sourceId, paint: { 'line-color': colors[index % colors.length], 'line-width': isSelected ? 8 : 5, 'line-opacity': isSelected ? 1.0 : 0.5 } });

            if (!isSelected && onRouteClick) {
                this.map.on('click', layerId, () => onRouteClick(index));
                this.map.on('mouseenter', layerId, () => { this.map.getCanvas().style.cursor = 'pointer'; });
                this.map.on('mouseleave', layerId, () => { this.map.getCanvas().style.cursor = ''; });
            }
        });

        if (routes[selectedIndex]?.coordinates) this.fitRouteToView(routes[selectedIndex].coordinates, fitOptions);
    }

    _clearAltRoutes() {
        for (let i = 0; i < 10; i++) {
            const lid = `alt-route-line-${i}`, sid = `alt-route-${i}`;
            if (this.map?.getLayer(lid)) this.map.removeLayer(lid);
            if (this.map?.getSource(sid)) this.map.removeSource(sid);
        }
    }

    // ── Wind Markers ─────────────────────────────────────────────────────

    addWindMarkers(windData) {
        this.windMarkers.forEach(m => m.remove());
        this.windMarkers = [];
        if (!windData || windData.length === 0) return;

        const worstWindPoint = windData.reduce((w, c) => ((c.wind?.speed || c.speed || 0) > (w.wind?.speed || w.speed || 0) ? c : w), windData[0]);

        const keyIndices = new Set();
        const total = windData.length;
        if (total > 2) {
            [0, 0.2, 0.4, 0.6, 0.8, 1].forEach(p => keyIndices.add(Math.min(Math.floor(total * p), total - 1)));
            windData.forEach((pt, i) => { if (pt.cautionExceeded || pt.dangerExceeded || pt.isPinnedWindPoint) keyIndices.add(i); });
            let wi = 0, ws = 0; windData.forEach((p, i) => { const s = p.wind?.speed || p.speed || 0; if (s > ws) { ws = s; wi = i; } }); keyIndices.add(wi);
        } else { windData.forEach((_, i) => keyIndices.add(i)); }

        Array.from(keyIndices).sort((a, b) => a - b).forEach(index => {
            const pt = windData[index];
            const speed = pt.wind?.speed || pt.speed || 0;
            const gust = pt.wind?.gust || pt.gust || speed;
            const dir = pt.wind?.direction || pt.direction || 0;
            const arrival = pt.arrivalTime;
            const isWorst = pt === worstWindPoint;
            const caution = pt.cautionExceeded || false;
            const danger = pt.dangerExceeded || false;

            const el = this._windEl(speed, arrival, isWorst, caution, danger);
            const marker = new maplibregl.Marker({ element: el }).setLngLat([pt.lng, pt.lat]).addTo(this.map);
            marker.setPopup(new maplibregl.Popup({ offset: 25 }).setHTML(this._windPopup(pt, speed, gust, dir, arrival, isWorst, caution, danger)));
            this.windMarkers.push(marker);
        });
        console.log(`Added ${this.windMarkers.length} wind markers`);
    }

    _windEl(speed, arrival, isWorst, caution, danger) {
        let color = '#4CAF50';
        if (danger) color = '#F44336'; else if (caution) color = '#FFA726';
        const time = arrival ? new Date(arrival).toLocaleTimeString(displayLocale(), { hour: 'numeric', minute: '2-digit', hour12: true }) : '';
        const badge = isWorst ? '<div style="position:absolute;top:-8px;right:-8px;background:#F44336;color:white;border-radius:50%;width:20px;height:20px;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:bold;border:2px solid white;">!</div>' : '';
        const el = document.createElement('div');
        el.className = 'wind-marker';
        el.innerHTML = `<div style="position:relative;background:${color};border-radius:8px;border:${isWorst ? '3px solid #F44336' : '2px solid white'};padding:6px 10px;font-size:12px;font-weight:bold;color:white;box-shadow:0 3px 8px rgba(0,0,0,0.4);white-space:nowrap;text-align:center;min-width:60px;">${badge}<div style="font-size:16px;line-height:1.2;font-weight:800;">${Math.round(units.speed(speed))} ${units.unit.speed()}</div>${time ? `<div style="font-size:9px;line-height:1.2;margin-top:2px;opacity:0.95;">${time}</div>` : ''}</div>`;
        return el;
    }

    _windPopup(pt, speed, gust, dir, arrival, isWorst, caution, danger) {
        const t = arrival ? new Date(arrival).toLocaleTimeString(displayLocale(), { hour: 'numeric', minute: '2-digit', hour12: true }) : 'N/A';
        const arrow = this.getWindDirectionArrow(dir);
        const dirText = this.getWindDirectionText(dir);
        let h = isWorst ? '<strong style="color:#F44336;">⚠️ WORST WIND ON ROUTE</strong><br>' : '';
        h += `<strong>Arrival:</strong> ${t}<br><strong>Wind:</strong> ${Math.round(units.speed(speed))} ${units.unit.speed()}<br><strong>Gusts:</strong> ${Math.round(units.speed(gust))} ${units.unit.speed()}<br><strong>Direction:</strong> <span style="font-size:1.5em;">${arrow}</span> ${dirText}<br>`;
        if (pt.cautionThreshold && pt.dangerThreshold) {
            h += `<br><strong>Thresholds:</strong><br>🟡 ${Math.round(units.speed(pt.cautionThreshold))} ${units.unit.speed()} · 🔴 ${Math.round(units.speed(pt.dangerThreshold))} ${units.unit.speed()}<br>`;
            if (danger) h += '<span style="color:#F44336;font-weight:bold;">🔴 DANGEROUS</span>';
            else if (caution) h += '<span style="color:#FFA726;font-weight:bold;">🟡 CAUTION</span>';
            else h += '<span style="color:#4CAF50;font-weight:bold;">🟢 Safe</span>';
        }
        h += `<br><br><a href="/wind-safety.html" target="_blank" style="color:#1e88e5;font-size:0.85em;">ℹ️ Threshold info</a>`;
        if (pt.isPinnedWindPoint && pt.originalSpeed !== undefined) {
            const ot = pt.originalArrivalTime ? new Date(pt.originalArrivalTime).toLocaleTimeString(displayLocale(), { hour: 'numeric', minute: '2-digit', hour12: true }) : '';
            h += `<br><br><div style="background:#f5f5f5;padding:0.4rem;border-radius:4px;font-size:0.85em;">📌 Was: ${Math.round(units.speed(pt.originalSpeed))} ${units.unit.speed()}${ot ? ' at ' + ot : ''}<br>Now: ${Math.round(units.speed(speed))} ${units.unit.speed()} at ${t}</div>`;
        }
        h += `<br><strong>Location:</strong> ${pt.lat.toFixed(4)}, ${pt.lng.toFixed(4)}`;
        return h;
    }

    // ── Gas Station Markers ──────────────────────────────────────────────

    addGasStationMarkers(stations, clearExisting = true) {
        if (clearExisting) { this.gasStationMarkers.forEach(m => m.remove()); this.gasStationMarkers = []; }
        stations.forEach(station => {
            const el = this._gasEl(station.isRvFriendly, station.source);
            const mid = `marker_${station.lat}_${station.lng}`;
            const marker = new maplibregl.Marker({ element: el }).setLngLat([station.lng, station.lat]).addTo(this.map);
            marker.setPopup(new maplibregl.Popup({ offset: 25, maxWidth: '280px' }).setHTML(this._gasPopup(station, mid)));
            marker._customId = mid;
            marker._stationData = station;
            this.gasStationMarkers.push(marker);
        });
    }

    _gasEl(isRvFriendly, source) {
        const color = source === 'curated' ? '#FFD700' : (isRvFriendly ? '#4CAF50' : '#2196F3');
        const icon = (source === 'curated' || isRvFriendly) ? '🚛' : '⛽';
        const el = document.createElement('div');
        el.className = 'gas-station-marker';
        el.innerHTML = `<div style="width:32px;height:32px;background:${color};border-radius:50%;border:3px solid white;display:flex;align-items:center;justify-content:center;font-size:16px;box-shadow:0 2px 5px rgba(0,0,0,0.3);cursor:pointer;">${icon}</div>`;
        return el;
    }

    _gasPopup(station, markerId) {
        // Station data comes from OpenStreetMap, which anyone can edit, so escape it all.
        const esc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
        const arg = (v) => esc(JSON.stringify(String(v ?? '')));
        const r = station.ratings || { yes: 0, no: 0, votes: 0 };
        let h = `<div style="min-width:220px;"><strong style="font-size:1.1em;">${esc(station.name)}</strong><br>`;
        if (station.brand) { const bn = { loves: "Love's", pilot: 'Pilot', flyingj: 'Flying J', ta: 'TA', petro: 'Petro' }; h += `<em>${esc(bn[station.brand] || station.brand)}</em><br>`; }
        if (station.travelerConfirmed) h += `<span style="color:#4CAF50;font-weight:bold;">${w(`✓ RV-Friendly — confirmed by ${r.yes} travelers`)}</span><br>`;
        else if (station.isRvFriendly) h += '<span style="color:#4CAF50;font-weight:bold;">✓ RV-Friendly</span><br>';
        if (!station.travelerConfirmed) h += `<small style="color:#666;">${r.votes > 0 ? w(`${r.yes} of ${r.votes} travelers report pull-through access`) : w('Not yet rated by travelers')}</small><br>`;
        if (station.distanceAlongRoute !== undefined) { h += `<br><strong>📍 ${units.distanceText(station.distanceAlongRoute)}</strong> from start`; if (station.distanceToDestination !== undefined) h += `<br><strong>🏁 ${units.distanceText(station.distanceToDestination)}</strong> to dest`; h += `<br><small>${station.distanceToRoute == null ? '? ' + (units.isMetric() ? 'km' : 'mi') : units.distanceText(station.distanceToRoute, { decimals: 1 })} off route</small>`; }
        if (station.highway) h += `<br><br><strong>Location:</strong> ${esc(station.highway)} Exit ${esc(station.exit)}<br>`;
        h += '<br><strong>Amenities:</strong><br>';
        const am = [];
        if (station.amenities) { if (station.amenities.diesel) am.push('⛽ Diesel'); if (station.amenities.rvDump) am.push(w('🚿 RV Dump')); if (station.amenities.propane) am.push(w('🔥 Propane')); if (station.amenities.restrooms) am.push('🚻 Restrooms'); if (station.amenities.showers) am.push('🚿 Showers'); if (station.amenities.restaurant) am.push('🍔 Restaurant'); if (station.amenities.wifi) am.push('📶 WiFi'); if (station.amenities.laundry) am.push('🧺 Laundry'); }
        h += am.length > 0 ? am.join('<br>') : 'No data';
        if (station.address) h += `<br><br><strong>Address:</strong><br>${esc(station.address)}`;
        if (station.phone) h += `<br><strong>Phone:</strong> <a href="tel:${esc(String(station.phone).replace(/[^\d+]/g, ''))}">${esc(station.phone)}</a>`;
        const ri = window.uiController?.selectedRouteIndex ?? window.uiController?.bestRouteIndex ?? 0;
        const rs = window.uiController?.getRouteStops(ri) || [];
        const added = rs.some(s => Math.abs(s.lat - station.lat) < 0.001 && Math.abs(s.lng - station.lng) < 0.001);
        if (added) { const si = rs.findIndex(s => Math.abs(s.lat - station.lat) < 0.001 && Math.abs(s.lng - station.lng) < 0.001); h += `<br><br><button onclick="window.removeGasStationStop(${si},'${markerId}')" style="width:100%;padding:8px;background:#F44336;color:white;border:none;border-radius:4px;cursor:pointer;font-weight:bold;">✕ Remove Stop</button>`; }
        else h += `<br><br><button onclick="window.addGasStationAsStop(${Number(station.lat)},${Number(station.lng)},${arg(station.name)},${arg(markerId)})" style="width:100%;padding:8px;background:#4CAF50;color:white;border:none;border-radius:4px;cursor:pointer;font-weight:bold;">➕ Add as Stop</button>`;
        h += `<br><button onclick="window.verifyFuelStop(${arg(station.verifyId || station.id)},${arg(station.name)})" style="width:100%;padding:6px;background:white;color:#1e88e5;border:2px solid #1e88e5;border-radius:4px;cursor:pointer;font-weight:600;font-size:0.8rem;margin-top:4px;">${w('✓ Rate this stop for RVs')}</button>`;
        if (station.source === 'curated') h += '<br><small style="color:#666;margin-top:8px;display:block;">Source: Curated</small>';
        h += '</div>';
        return h;
    }

    rebuildGasStationPopup(markerId) {
        const marker = this.gasStationMarkers.find(m => m._customId === markerId);
        if (!marker?._stationData) return;
        marker.getPopup()?.setHTML(this._gasPopup(marker._stationData, markerId));
    }

    clearGasStationMarkers() { this.gasStationMarkers.forEach(m => m.remove()); this.gasStationMarkers = []; }

    // ── Road Quality Overlay ─────────────────────────────────────────────

    drawRoadQualityOverlay(segments, routeCoords) {
        this.clearRoadQualityOverlay();
        const features = [];
        for (const seg of segments) {
            if (!seg.startLat || !seg.startLng || !seg.endLat || !seg.endLng) continue;
            if (seg.iriScore < 170) continue;
            if (!isSegmentOnRoute(seg, routeCoords)) continue;
            features.push({ type: 'Feature', properties: { color: seg.iriScore < 220 ? '#eab308' : '#ef4444', weight: seg.iriScore >= 220 ? 12 : 10 }, geometry: { type: 'LineString', coordinates: [[seg.startLng, seg.startLat], [seg.endLng, seg.endLat]] } });
        }
        const data = { type: 'FeatureCollection', features };
        if (this.roadQualitySourceAdded && this.map?.getSource('road-quality')) {
            this.map.getSource('road-quality').setData(data);
        } else {
            // Map not ready yet — apply when load fires
            this.map.on('load', () => {
                if (this.map.getSource('road-quality')) {
                    this.map.getSource('road-quality').setData(data);
                }
            });
        }
        console.log(`🛣️ Drew ${features.length} road quality segments`);
    }

    clearRoadQualityOverlay() {
        if (this.roadQualitySourceAdded && this.map?.getSource('road-quality')) {
            this.map.getSource('road-quality').setData({ type: 'FeatureCollection', features: [] });
        }
    }

    // ── Compatibility helpers for UIController ───────────────────────────

    createLayerGroup() {
        const markers = [];
        return { markers, _layers: markers, _mlMap: this.map, addTo() { return this; }, addLayer(m) { markers.push(m); }, clearLayers() { markers.forEach(m => m?.remove()); markers.length = 0; } };
    }

    createHtmlMarker(lat, lng, html, options = {}) {
        const el = document.createElement('div'); el.innerHTML = html; el.style.cursor = 'pointer';
        const m = new maplibregl.Marker({ element: el }).setLngLat([lng, lat]).addTo(this.map);
        if (options.popup) m.setPopup(new maplibregl.Popup({ offset: 25 }).setHTML(options.popup));
        return m;
    }

    createImageMarker(lat, lng, iconUrl, options = {}) {
        const sz = options.size || 32;
        const el = document.createElement('div'); el.innerHTML = `<img src="${iconUrl}" style="width:${sz}px;height:${sz}px;" />`; el.style.cursor = 'pointer';
        const m = new maplibregl.Marker({ element: el }).setLngLat([lng, lat]).addTo(this.map);
        if (options.popup) m.setPopup(new maplibregl.Popup({ offset: sz / 2, maxWidth: '300px' }).setHTML(options.popup));
        return m;
    }

    // ── Utilities ────────────────────────────────────────────────────────

    getMap() { return this.map; }

    getWindDirectionArrow(d) {
        d = ((d % 360) + 360) % 360;
        if (d >= 337.5 || d < 22.5) return '↓'; if (d < 67.5) return '↙'; if (d < 112.5) return '←';
        if (d < 157.5) return '↖'; if (d < 202.5) return '↑'; if (d < 247.5) return '↗';
        if (d < 292.5) return '→'; return '↘';
    }

    getWindDirectionText(d) {
        d = ((d % 360) + 360) % 360;
        if (d >= 337.5 || d < 22.5) return 'North'; if (d < 67.5) return 'Northeast'; if (d < 112.5) return 'East';
        if (d < 157.5) return 'Southeast'; if (d < 202.5) return 'South'; if (d < 247.5) return 'Southwest';
        if (d < 292.5) return 'West'; return 'Northwest';
    }
}
